import { action, type IObservableArray, observable, override, makeObservable } from 'mobx';
import { Translate } from '../../i18n';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { addStyles, DefaultTheme } from '../theme';
import { InputState } from './input.state';
import { Logger } from '../../services';

export const CSS = {
	container: 'c-Select',
	buttonContainer: 'c-Select__container',
	option: 'c-Select__option',
	label: 'c-Select__label',
	button: 'c-Select__button',
	error: 'c-Select__error',
	buttonDropDown: 'c-Select__drop',
	buttonText: 'c-Select__text',

	native: 'c-Select__native',

	isFullWidth: 'c-Select--full-width',
	isFocused: 'c-Select--is-focused',
	isDirty: 'c-Select--is-dirty',
	isDisabled: 'c-Select--is-disabled',
	isInvalid: 'c-Select--is-invalid',
	isOpen: 'c-Select--is-open',
	isFilled: 'c-Select--is-filled',
	isEmpty: 'c-Select--is-empty',

	MDL: {
		error: 'mdl-textfield__error',
		label: 'mdl-textfield__label',
		labelFloating: 'mdl-textfield--floating-label',
		button: 'mdl-button',
		isFocused: 'is-focused',
		isDirty: 'is-dirty',
		isUpgraded: 'is-upgraded',
		isInvalid: 'is-invalid',
		hasPlaceholder: 'has-placeholder',
	},
};

const padding = (top: number, right: number, bottom: number, left: number) =>
	`${DefaultTheme.space(top)} ${DefaultTheme.space(right)} ${DefaultTheme.space(bottom)} ${DefaultTheme.space(left)}`;

export const Style = {
	[CSS.container]: {
		'background-image': `url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='5' viewBox='7 10 10 5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%230' fill-rule='evenodd' opacity='.54' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
		'background-repeat': 'no-repeat',
		'box-sizing': 'border-box',
		'background-position': 'right 8px bottom 16px',
		position: 'relative',
		width: '300px',
		'max-width': '100%',
		height: '56px',
	},
	[CSS.container + ' .' + CSS.label]: {
		color: DefaultTheme.font.color.disabled,
		'text-overflow': 'ellipsis',
		'font-weight': 'normal',
	},
	[CSS.native]: {
		'background-color': 'transparent',
		'font-family': DefaultTheme.font.family,
		color: DefaultTheme.font.color.supporting,
		outline: 'none',
		'white-space': 'nowrap',
		cursor: 'pointer',
		border: 'none',
		'border-bottom': '1px solid ' + DefaultTheme.font.color.divider,
		padding: padding(6, 6, 1, 0),
		'line-height': '1.75rem',
		'font-size': '1rem',
		appearance: 'none', // Hide drop-down button
		width: '100%',
		'&::-ms-expand': {
			display: 'none',
		},
	},

	[CSS.isDisabled]: {
		cursor: 'default',
		pointerEvents: 'none',
		opacity: 0.3,
	},
};

addStyles(Style);

export class SelectInputState<T extends string = string> extends InputState<T> {
	readonly options: IObservableArray<SelectInputOption<T>>;

	constructor(label: string, options: SelectInputOption<T>[] = []) {
		super(label);
		makeObservable(this);

		this.options = observable.array(options, { deep: false });

		// If "isRequired" is enabled later, select the first option by default.
		this.reaction(
			() => this.isRequired,
			() => {
				if (this.value) {
					// An option is already selected.
					return;
				}
				if (!this.options?.[0]) {
					// There are no options, but isRequired is true -- this is an error.
					Logger.error(this, 'SelectInputState has required=true but no options are set');
					return;
				}
				this.setValue(this.options[0].value);
			}
		);
	}

	// When resetting a select input with isRequired, we need to select the first option again.
	@override override reset() {
		super.reset();

		if (this.isRequired && this.options[0]) {
			this.setValue(this.options[0].value);
		}
	}

	@action addOption(option: SelectInputOption<T>) {
		this.options.push(option);
	}

	@action setOptions(options: SelectInputOption<T>[]) {
		this.options.replace(options);
	}
}

export interface SelectInputOption<T extends string = string> {
	key: string;
	value: T;
}

export const SelectInputComponent = {
	view(state: SelectInputState): MithrilVNode {
		let className = CSS.container + ' ' + CSS.MDL.labelFloating;
		if (state.placeholder || state.isRequired) {
			className += ' ' + CSS.MDL.hasPlaceholder;
		}
		if (state.isFilled) {
			className += ' ' + CSS.isFilled;
			className += ' ' + CSS.MDL.isDirty;
		} else {
			className += ' ' + CSS.isEmpty;
		}
		if (state.focused) {
			className += ' ' + CSS.isFocused;
			className += ' ' + CSS.MDL.isFocused;
		}

		if (state.disabled) {
			className = `${className} ${CSS.isDisabled}`;
		}
		if (state.touched && !state.isValid) {
			className += ' ' + CSS.isInvalid;
			className += ' ' + CSS.MDL.isInvalid;
		}
		const EMPTY_VALUE = state.id + '-empty';

		const options = !state.isRequired ? [m('option', { selected: true, value: EMPTY_VALUE }, state.placeholder)] : [];

		for (const opt of state.options) {
			options.push(
				m('option', { className: CSS.option, value: opt.value, selected: opt.value === state.value }, opt.key)
			);
		}
		const children: MithrilVNode[] = [];

		if (state.isLabelShown) {
			children.push(
				m(
					'label',
					{ className: CSS.label + ' ' + CSS.MDL.label, title: Translate(state.label), for: state.id },
					Translate(state.label)
				)
			);
		}

		children.push(
			m(
				'select',
				{
					id: state.id,
					className: CSS.native,
					onchange: (e) => {
						const target = e.target as HTMLInputElement;
						const value = target.value;
						if (value === EMPTY_VALUE) {
							state.setValue(null);
						} else {
							state.setValue(value);
						}
					},
					onfocus: () => state.setFocused(true),
					onblur: () => {
						state.setFocused(false);
						state.setTouched();
					},
					disabled: state.disabled,
				},
				options
			)
		);

		// Show error message when touched and invalid
		if (state.touched && !state.isValid && state.hasError()) {
			children.push(m('span', { className: CSS.error + ' ' + CSS.MDL.error }, Translate(state.error)));
		}

		return m('div', { className, 'data-testid': `select-${state.id}` }, children);
	},
};
